
import React, { useMemo, useState } from "react";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface Address {
  id: string;
  label: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface AcademicRecord {
  id: string;
  institution: string;
  degree: string;
  fieldOfStudy: string;
  startYear: number;
  endYear: number | null;
}

export interface UpdateHistoryEntry {
  id: string;
  timestamp: string;
  field: string;
  previousValue: string;
  newValue: string;
  updatedBy: string;
}

export interface PersonRecord {
  name: string;
  birthdate: string;
  description: string;
  addresses: Address[];
  academics: AcademicRecord[];
  updateHistory: UpdateHistoryEntry[];
}

/* ------------------------------------------------------------------ */
/*  Sample data                                                        */
/* ------------------------------------------------------------------ */

const sampleMain: PersonRecord = {
  name: "Elena Marchetti",
  birthdate: "1988-03-22",
  description: "Marine biologist focused on coral reef restoration in the Mediterranean.",
  addresses: [
    {
      id: "addr-1",
      label: "Home",
      street: "Via dei Pescatori 14",
      city: "Genoa",
      state: "Liguria",
      postalCode: "16126",
      country: "Italy",
    },
    {
      id: "addr-2",
      label: "Work",
      street: "Stazione Zoologica Anton Dohrn",
      city: "Naples",
      state: "Campania",
      postalCode: "80121",
      country: "Italy",
    },
  ],
  academics: [
    {
      id: "ac-1",
      institution: "University of Genoa",
      degree: "B.Sc.",
      fieldOfStudy: "Marine Biology",
      startYear: 2006,
      endYear: 2010,
    },
    {
      id: "ac-2",
      institution: "University of Naples Federico II",
      degree: "Ph.D.",
      fieldOfStudy: "Marine Ecology",
      startYear: 2011,
      endYear: 2015,
    },
  ],
  updateHistory: [
    {
      id: "hist-1",
      timestamp: "2023-05-10T09:00:00Z",
      field: "description",
      previousValue: "Marine biologist.",
      newValue:
        "Marine biologist focused on coral reef restoration in the Mediterranean.",
      updatedBy: "M. Rossi",
    },
  ],
};

const sampleCompare: PersonRecord = {
  name: "Elena Marchetti",
  birthdate: "1988-03-22",
  description: "Senior marine biologist leading coral reef restoration projects across the Mediterranean and Red Sea.",
  addresses: [
    {
      id: "addr-1",
      label: "Home",
      street: "Via dei Pescatori 14",
      city: "Genoa",
      state: "Liguria",
      postalCode: "16126",
      country: "Italy",
    },
    {
      id: "addr-3",
      label: "Field station",
      street: "Marine Research Station",
      city: "Eilat",
      state: "",
      postalCode: "8810302",
      country: "Israel",
    },
  ],
  academics: [
    {
      id: "ac-1",
      institution: "University of Genoa",
      degree: "B.Sc.",
      fieldOfStudy: "Marine Biology",
      startYear: 2006,
      endYear: 2010,
    },
    {
      id: "ac-2",
      institution: "University of Naples Federico II",
      degree: "Ph.D.",
      fieldOfStudy: "Marine Ecology",
      startYear: 2011,
      endYear: 2015,
    },
    {
      id: "ac-3",
      institution: "Interuniversity Institute of Marine Sciences",
      degree: "Postdoc",
      fieldOfStudy: "Coral Reef Genomics",
      startYear: 2016,
      endYear: 2018,
    },
  ],
  updateHistory: [
    {
      id: "hist-1",
      timestamp: "2023-05-10T09:00:00Z",
      field: "description",
      previousValue: "Marine biologist.",
      newValue:
        "Marine biologist focused on coral reef restoration in the Mediterranean.",
      updatedBy: "M. Rossi",
    },
    {
      id: "hist-2",
      timestamp: "2024-11-02T14:30:00Z",
      field: "description",
      previousValue:
        "Marine biologist focused on coral reef restoration in the Mediterranean.",
      newValue:
        "Senior marine biologist leading coral reef restoration projects across the Mediterranean and Red Sea.",
      updatedBy: "A. Haddad",
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Small icons (inline SVG, no dependency)                            */
/* ------------------------------------------------------------------ */

const IconCheck = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" aria-hidden="true">
    <path
      d="M4 10.5l3.5 3.5L16 5.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconArrowLeft = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" aria-hidden="true">
    <path
      d="M12.5 5L7 10l5.5 5M7.5 10H16"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconPlus = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" aria-hidden="true">
    <path
      d="M10 4v12M4 10h12"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const IconTrash = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" aria-hidden="true">
    <path
      d="M5 6h10M8.5 6V4.5h3V6M6 6l.6 9.2a1 1 0 001 .8h4.8a1 1 0 001-.8L14 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconAlert = ({ className = "" }: { className?: string }) => (
  <svg viewBox="0 0 20 20" className={className} fill="none" aria-hidden="true">
    <path
      d="M10 3.5l7.5 13h-15L10 3.5z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M10 8.2v3.6M10 14.4h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Diff helpers                                                       */
/* ------------------------------------------------------------------ */

type WithId = { id: string };

function stripId<T extends WithId>(item: T): Omit<T, "id"> {
  const { id, ...rest } = item;
  return rest;
}

function sameContent<T extends WithId>(a: T, b: T): boolean {
  return JSON.stringify(stripId(a)) === JSON.stringify(stripId(b));
}

function findMatch<T extends WithId>(item: T, list: T[]): T | undefined {
  return list.find((candidate) => sameContent(candidate, item));
}

function cloneRecord(rec: PersonRecord): PersonRecord {
  return JSON.parse(JSON.stringify(rec));
}

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

/* ------------------------------------------------------------------ */
/*  Presentational primitives                                          */
/* ------------------------------------------------------------------ */

function ValuePane({ status, children, }: {
  status: "match" | "differ" | "neutral";
  children: React.ReactNode;
}) {
  const styles =
    status === "differ"
      ? "border-(--flag)/40 bg-(--flag-bg)"
      : status === "match"
      ? "border-(--match)/30 bg-(--match-bg)"
      : "border-(--rule) bg-(--paper-raised)";
  return (
    <div className={`${styles} rounded-sm border px-3.5 py-2.5 text-[13.5px] leading-relaxed text-(--ink)`}>
      {children}
    </div>
  );
}

function SectionHeading({ title, note, }: {
  title: string;
  note?: string;
}) {
  return (
    <div className="flex items-baseline justify-between border-b border-(--rule) pb-2 mb-4">
      <h2
        className="text-[19px] tracking-tight text-(--ink)"
        style={{ fontFamily: "var(--serif)" }}
      >
        {title}
      </h2>
      {note && (
        <span
          className="text-[11.5px] text-(--ink-soft)"
          style={{ fontFamily: "var(--mono)" }}
        >
          {note}
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Scalar field row (name / birthdate / description)                  */
/* ------------------------------------------------------------------ */

function EditableValue({
  status,
  value,
  onChange,
  multiline = false,
  inputType = "text",
}: {
  status: "match" | "differ";
  value: string;
  onChange: (v: string) => void;
  multiline?: boolean;
  inputType?: "text" | "date";
}) {
  const styles =
    status === "differ"
      ? "border-(--flag)/50 bg-(--flag-bg) focus:border-(--flag)"
      : "border-(--match)/40 bg-(--match-bg) focus:border-(--match)";
  const shared = `w-full rounded-sm border px-3.5 py-2.5 text-[13.5px] leading-relaxed text-(--ink) outline-none transition-colors ${styles}`;

  if (multiline) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className={`${shared} resize-y`}
      />
    );
  }
  return (
    <input
      type={inputType}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={shared}
    />
  );
}

function ScalarRow({
  label,
  mainValue,
  compareValue,
  onMainChange,
  onAdopt,
  multiline = false,
  inputType = "text",
}: {
  label: string;
  mainValue: string;
  compareValue: string;
  onMainChange: (v: string) => void;
  onAdopt: () => void;
  multiline?: boolean;
  inputType?: "text" | "date";
}) {
  const differs = mainValue !== compareValue;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_128px_1fr] gap-2.5 lg:gap-4 items-stretch py-3 border-b border-(--rule) last:border-b-0">
      <div className="flex flex-col gap-1">
        <span
          className="text-[10.5px] uppercase-none text-(--ink-soft)"
          style={{ fontFamily: "var(--mono)" }}
        >
          {label} · editable
        </span>
        <EditableValue
          status={differs ? "differ" : "match"}
          value={mainValue}
          onChange={onMainChange}
          multiline={multiline}
          inputType={inputType}
        />
      </div>

      <div className="flex lg:flex-col items-center justify-center gap-1.5 lg:gap-2 py-1">
        {differs ? (
          <button
            onClick={onAdopt}
            className="group flex items-center gap-1.5 rounded-sm border border-(--flag) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--flag) transition-colors hover:bg-(--flag) hover:text-(--paper-raised)"
            style={{ fontFamily: "var(--mono)" }}
            title="Adopt the compare-side value into Main"
          >
            <IconArrowLeft className="h-3.5 w-3.5 rotate-180 lg:rotate-0 group-hover:-translate-x-0.5 transition-transform" />
            adopt
          </button>
        ) : (
          <span
            className="flex items-center gap-1 text-[11px] text-(--match)"
            style={{ fontFamily: "var(--mono)" }}
          >
            <IconCheck className="h-3.5 w-3.5" />
            match
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <span
          className="text-[10.5px] text-(--ink-soft) lg:text-right"
          style={{ fontFamily: "var(--mono)" }}
        >
          {label}
        </span>
        <ValuePane status={differs ? "differ" : "neutral"}>{compareValue}</ValuePane>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Generic list-field section (addresses / academics / history)       */
/* ------------------------------------------------------------------ */

interface ArraySectionProps<T extends WithId> {
  title: string;
  mainItems: T[];
  compareItems: T[];
  renderSummary: (item: T) => React.ReactNode;
  renderEditable: (item: T, onChange: (patch: Partial<T>) => void) => React.ReactNode;
  onMergeSelected: (items: T[]) => void;
  onReplaceAll: () => void;
  onRemoveFromMain: (id: string) => void;
  onUpdateItem: (id: string, patch: Partial<T>) => void;
  onAddBlank: () => void;
  addLabel: string;
}

function ArraySection<T extends WithId>({
  title,
  mainItems,
  compareItems,
  renderSummary,
  renderEditable,
  onMergeSelected,
  onReplaceAll,
  onRemoveFromMain,
  onUpdateItem,
  onAddBlank,
  addLabel,
}: ArraySectionProps<T>) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const newFromCompare = useMemo(
    () => compareItems.filter((c) => !findMatch(c, mainItems)),
    [compareItems, mainItems]
  );

  const matchedIds = useMemo(
    () => new Set(compareItems.filter((c) => findMatch(c, mainItems)).map((c) => c.id)),
    [compareItems, mainItems]
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function mergeSelected() {
    const items = newFromCompare.filter((i) => selected.has(i.id));
    if (items.length === 0) return;
    onMergeSelected(items);
    setSelected(new Set());
  }

  const diffCount = newFromCompare.length;

  return (
    <section className="mb-9">
      <SectionHeading
        title={title}
        note={
          diffCount > 0
            ? `${diffCount} new on compare side`
            : "no new items"
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_128px_1fr] gap-4">
        {/* MAIN column — directly editable */}
        <div className="flex flex-col gap-2">
          {mainItems.length === 0 && (
            <p className="text-[13px] text-(--ink-soft) italic">Empty.</p>
          )}
          {mainItems.map((item) => (
            <div
              key={item.id}
              className="group relative rounded-sm border border-(--rule) bg-(--paper-raised) px-3.5 py-3"
            >
              <div className="pr-6">
                {renderEditable(item, (patch) => onUpdateItem(item.id, patch))}
              </div>
              <button
                onClick={() => onRemoveFromMain(item.id)}
                className="absolute right-2 top-2 text-(--ink-soft) opacity-0 transition-opacity hover:text-(--flag) group-hover:opacity-100"
                title="Remove from main"
                aria-label="Remove from main"
              >
                <IconTrash className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            onClick={onAddBlank}
            className="flex items-center justify-center gap-1.5 rounded-sm border border-dashed border-(--rule) px-3 py-2 text-[11.5px] text-(--ink-soft) transition-colors hover:border-(--navy) hover:text-(--navy)"
            style={{ fontFamily: "var(--mono)" }}
          >
            <IconPlus className="h-3.5 w-3.5" />
            {addLabel}
          </button>
        </div>

        {/* CONTROLS column */}
        <div className="flex lg:flex-col items-center justify-start gap-2 py-1 flex-wrap">
          <button
            onClick={mergeSelected}
            disabled={selected.size === 0}
            className="flex items-center gap-1.5 rounded-sm border border-(--navy) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--navy) transition-colors enabled:hover:bg-(--navy) enabled:hover:text-(--paper-raised) disabled:cursor-not-allowed disabled:opacity-35"
            style={{ fontFamily: "var(--mono)" }}
            title="Add the checked compare-side items to Main"
          >
            <IconPlus className="h-3.5 w-3.5" />
            merge ({selected.size})
          </button>
          <button
            onClick={onReplaceAll}
            className="rounded-sm border border-(--flag) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--flag) transition-colors hover:bg-(--flag) hover:text-(--paper-raised)"
            style={{ fontFamily: "var(--mono)" }}
            title="Replace Main's entire list with the compare-side list"
          >
            replace all
          </button>
        </div>

        {/* COMPARE column */}
        <div className="flex flex-col gap-2">
          {compareItems.length === 0 && (
            <p className="text-[13px] text-(--ink-soft) italic">Empty.</p>
          )}
          {compareItems.map((item) => {
            const isMatched = matchedIds.has(item.id);
            return (
              <label
                key={item.id}
                className={`flex items-start gap-2.5 rounded-sm border px-3.5 py-2.5 ${
                  isMatched
                    ? "border-(--match)/30 bg-(--match-bg)"
                    : "border-(--flag)/40 bg-(--flag-bg) cursor-pointer"
                }`}
              >
                {isMatched ? (
                  <IconCheck className="mt-0.5 h-4 w-4 shrink-0 text-(--match)" />
                ) : (
                  <input
                    type="checkbox"
                    checked={selected.has(item.id)}
                    onChange={() => toggle(item.id)}
                    className="mt-1 h-3.5 w-3.5 shrink-0 accent-(--flag)"
                  />
                )}
                <div className="flex-1">{renderSummary(item)}</div>
              </label>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Item renderers                                                     */
/* ------------------------------------------------------------------ */

function fieldLine(label: string, value: React.ReactNode) {
  return (
    <div className="flex gap-2 text-[13px] leading-relaxed">
      <span
        className="w-18.5 shrink-0 text-[10.5px] uppercase-none text-(--ink-soft) pt-0.75"
        style={{ fontFamily: "var(--mono)" }}
      >
        {label}
      </span>
      <span className="text-(--ink)">{value}</span>
    </div>
  );
}

function MiniField({
  label,
  value,
  onChange,
  type = "text",
  span = 1,
}: {
  label: string;
  value: string | number;
  onChange: (v: string) => void;
  type?: "text" | "number";
  span?: 1 | 2;
}) {
  return (
    <label
      className={`flex flex-col gap-0.5 ${span === 2 ? "col-span-2" : "col-span-1"}`}
    >
      <span
        className="text-[9.5px] uppercase-none text-(--ink-soft)"
        style={{ fontFamily: "var(--mono)" }}
      >
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-sm border border-(--rule) bg-(--paper) px-2 py-1.5 text-[12.5px] text-(--ink) outline-none focus:border-(--navy)"
      />
    </label>
  );
}

function renderAddress(a: Address) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[13.5px] font-medium text-(--ink)">{a.label}</span>
      <span className="text-[13px] text-(--ink-soft)">
        {a.street}, {a.city}
        {a.state ? `, ${a.state}` : ""} {a.postalCode}, {a.country}
      </span>
    </div>
  );
}

function renderAddressEditable(a: Address, onChange: (patch: Partial<Address>) => void) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <MiniField label="label" value={a.label} onChange={(v) => onChange({ label: v })} span={2} />
      <MiniField label="street" value={a.street} onChange={(v) => onChange({ street: v })} span={2} />
      <MiniField label="city" value={a.city} onChange={(v) => onChange({ city: v })} />
      <MiniField label="state" value={a.state} onChange={(v) => onChange({ state: v })} />
      <MiniField label="postal code" value={a.postalCode} onChange={(v) => onChange({ postalCode: v })} />
      <MiniField label="country" value={a.country} onChange={(v) => onChange({ country: v })} />
    </div>
  );
}

function renderAcademic(a: AcademicRecord) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[13.5px] font-medium text-(--ink)">
        {a.degree} · {a.fieldOfStudy}
      </span>
      <span className="text-[13px] text-(--ink-soft)">
        {a.institution} ({a.startYear}–{a.endYear ?? "present"})
      </span>
    </div>
  );
}

function renderAcademicEditable(
  a: AcademicRecord,
  onChange: (patch: Partial<AcademicRecord>) => void
) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <MiniField label="institution" value={a.institution} onChange={(v) => onChange({ institution: v })} span={2} />
      <MiniField label="degree" value={a.degree} onChange={(v) => onChange({ degree: v })} />
      <MiniField label="field of study" value={a.fieldOfStudy} onChange={(v) => onChange({ fieldOfStudy: v })} />
      <MiniField
        label="start year"
        type="number"
        value={a.startYear}
        onChange={(v) => onChange({ startYear: Number(v) || 0 })}
      />
      <MiniField
        label="end year"
        type="number"
        value={a.endYear ?? ""}
        onChange={(v) => onChange({ endYear: v === "" ? null : Number(v) })}
      />
    </div>
  );
}

function renderHistory(h: UpdateHistoryEntry) {
  const date = new Date(h.timestamp);
  const formatted = isNaN(date.getTime())
    ? h.timestamp
    : date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-[12px] text-(--ink-soft)" style={{ fontFamily: "var(--mono)" }}>
        <span>{formatted}</span>
        <span>·</span>
        <span>{h.updatedBy}</span>
        <span>·</span>
        <span>{h.field}</span>
      </div>
      <span className="text-[13px] text-(--ink)">{h.newValue}</span>
    </div>
  );
}

function renderHistoryEditable(
  h: UpdateHistoryEntry,
  onChange: (patch: Partial<UpdateHistoryEntry>) => void
) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <MiniField
        label="timestamp"
        value={h.timestamp}
        onChange={(v) => onChange({ timestamp: v })}
      />
      <MiniField label="updated by" value={h.updatedBy} onChange={(v) => onChange({ updatedBy: v })} />
      <MiniField label="field" value={h.field} onChange={(v) => onChange({ field: v })} span={2} />
      <MiniField
        label="previous value"
        value={h.previousValue}
        onChange={(v) => onChange({ previousValue: v })}
        span={2}
      />
      <MiniField
        label="new value"
        value={h.newValue}
        onChange={(v) => onChange({ newValue: v })}
        span={2}
      />
    </div>
  );
}

function makeBlankAddress(): Address {
  return {
    id: uid("addr"),
    label: "New address",
    street: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  };
}

function makeBlankAcademic(): AcademicRecord {
  return {
    id: uid("ac"),
    institution: "",
    degree: "",
    fieldOfStudy: "",
    startYear: new Date().getFullYear(),
    endYear: null,
  };
}

function makeBlankHistory(): UpdateHistoryEntry {
  const now = new Date().toISOString();
  return {
    id: uid("hist"),
    timestamp: now,
    field: "",
    previousValue: "",
    newValue: "",
    updatedBy: "",
  };
}

/* ------------------------------------------------------------------ */
/*  JSON edit panel                                                     */
/* ------------------------------------------------------------------ */

function JsonEditPanel({
  main,
  compare,
  onApply,
}: {
  main: PersonRecord;
  compare: PersonRecord;
  onApply: (main: PersonRecord, compare: PersonRecord) => void;
}) {
  const [mainText, setMainText] = useState(JSON.stringify(main, null, 2));
  const [compareText, setCompareText] = useState(JSON.stringify(compare, null, 2));
  const [error, setError] = useState<string | null>(null);

  function apply() {
    try {
      const parsedMain = JSON.parse(mainText);
      const parsedCompare = JSON.parse(compareText);
      setError(null);
      onApply(parsedMain, parsedCompare);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Invalid JSON.");
    }
  }

  return (
    <div className="mb-8 rounded-sm border border-(--rule) bg-(--paper-raised) p-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <label
            className="mb-1.5 block text-[10.5px] text-(--ink-soft)"
            style={{ fontFamily: "var(--mono)" }}
          >
            main record json
          </label>
          <textarea
            value={mainText}
            onChange={(e) => setMainText(e.target.value)}
            spellCheck={false}
            rows={10}
            className="w-full resize-y rounded-sm border border-(--rule) bg-(--paper) p-3 text-[12px] text-(--ink) outline-none focus:border-(--navy)"
            style={{ fontFamily: "var(--mono)" }}
          />
        </div>
        <div>
          <label
            className="mb-1.5 block text-[10.5px] text-(--ink-soft)"
            style={{ fontFamily: "var(--mono)" }}
          >
            compare record json
          </label>
          <textarea
            value={compareText}
            onChange={(e) => setCompareText(e.target.value)}
            spellCheck={false}
            rows={10}
            className="w-full resize-y rounded-sm border border-(--rule) bg-(--paper) p-3 text-[12px] text-(--ink) outline-none focus:border-(--navy)"
            style={{ fontFamily: "var(--mono)" }}
          />
        </div>
      </div>

      {error && (
        <div
          className="mt-3 flex items-center gap-2 rounded-sm border border-(--flag)/40 bg-(--flag-bg) px-3 py-2 text-[12.5px] text-(--flag)"
          style={{ fontFamily: "var(--mono)" }}
        >
          <IconAlert className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <button
          onClick={apply}
          className="rounded-sm border border-(--navy) bg-(--navy) px-4 py-2 text-[12.5px] text-(--paper-raised) transition-opacity hover:opacity-90"
          style={{ fontFamily: "var(--mono)" }}
        >
          apply json
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Design tokens (ledger palette)                                     */
/* ------------------------------------------------------------------ */

const tokens: React.CSSProperties = {
  ["--paper" as any]: "#F7F5EF",
  ["--paper-raised" as any]: "#FFFFFF",
  ["--ink" as any]: "#20261F",
  ["--ink-soft" as any]: "#5B6058",
  ["--rule" as any]: "#D8D2C0",
  ["--flag" as any]: "#B5541F",
  ["--flag-bg" as any]: "#F3E3D6",
  ["--match" as any]: "#3F6C4F",
  ["--match-bg" as any]: "#E4EDE2",
  ["--navy" as any]: "#233B52",
  ["--navy-bg" as any]: "#E4E9EE",
  ["--serif" as any]: "'Source Serif 4', 'Iowan Old Style', Georgia, 'Times New Roman', serif",
  ["--mono" as any]: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
};


/* ------------------------------------------------------------------ */
/*  Root component                                                     */
/* ------------------------------------------------------------------ */

export default function JsonRecordCompare2() {
  const [main, setMain] = useState<PersonRecord>(sampleMain);
  const [compare, setCompare] = useState<PersonRecord>(sampleCompare);
  const [showEditor, setShowEditor] = useState(false);
  const [copied, setCopied] = useState(false);

  const scalarDiffs = useMemo(() => {
    let n = 0;
    if (main.name !== compare.name) n++;
    if (main.birthdate !== compare.birthdate) n++;
    if (main.description !== compare.description) n++;
    return n;
  }, [main, compare]);

  const newAddressCount = useMemo(
    () => compare.addresses.filter((c) => !findMatch(c, main.addresses)).length,
    [compare.addresses, main.addresses]
  );
  const newAcademicCount = useMemo(
    () => compare.academics.filter((c) => !findMatch(c, main.academics)).length,
    [compare.academics, main.academics]
  );
  const newHistoryCount = useMemo(
    () => compare.updateHistory.filter((c) => !findMatch(c, main.updateHistory)).length,
    [compare.updateHistory, main.updateHistory]
  );

  const totalDiffs = scalarDiffs + newAddressCount + newAcademicCount + newHistoryCount;

  function adoptScalar(field: "name" | "birthdate" | "description") {
    setMain((prev) => ({ ...prev, [field]: compare[field] }));
  }

  function mergeAddresses(items: Address[]) {
    setMain((prev) => ({
      ...prev,
      addresses: [...prev.addresses, ...items.map((i) => ({ ...i, id: i.id || uid("addr") }))],
    }));
  }
  function mergeAcademics(items: AcademicRecord[]) {
    setMain((prev) => ({
      ...prev,
      academics: [...prev.academics, ...items.map((i) => ({ ...i, id: i.id || uid("ac") }))],
    }));
  }
  function mergeHistory(items: UpdateHistoryEntry[]) {
    setMain((prev) => ({
      ...prev,
      updateHistory: [...prev.updateHistory, ...items.map((i) => ({ ...i, id: i.id || uid("hist") }))],
    }));
  }

  function copyMerged() {
    navigator.clipboard?.writeText(JSON.stringify(main, null, 2)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }

  function downloadMerged() {
    const blob = new Blob([JSON.stringify(main, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "merged-record.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function resetSamples() {
    setMain(cloneRecord(sampleMain));
    setCompare(cloneRecord(sampleCompare));
  }

  return (
    <div
      style={tokens}
      className="min-h-screen bg-(--paper) px-4 py-8 sm:px-8 lg:px-12"
    >
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <header className="mb-8 flex flex-col gap-4 border-b border-(--rule) pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1
              className="text-[28px] leading-tight text-(--ink)"
              style={{ fontFamily: "var(--serif)" }}
            >
              Record comparison
            </h1>
            <p className="mt-1 text-[13.5px] text-(--ink-soft)">
              {totalDiffs === 0
                ? "The two records match on every field."
                : `${totalDiffs} field${totalDiffs === 1 ? "" : "s"} differ between Main and Compare.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowEditor((v) => !v)}
              className="rounded-sm border border-(--rule) bg-(--paper-raised) px-3 py-1.5 text-[12px] text-(--ink) transition-colors hover:border-(--navy)"
              style={{ fontFamily: "var(--mono)" }}
            >
              {showEditor ? "hide json editor" : "edit source json"}
            </button>
            <button
              onClick={resetSamples}
              className="rounded-sm border border-(--rule) bg-(--paper-raised) px-3 py-1.5 text-[12px] text-(--ink) transition-colors hover:border-(--navy)"
              style={{ fontFamily: "var(--mono)" }}
            >
              reset sample data
            </button>
          </div>
        </header>

        {showEditor && (
          <JsonEditPanel
            main={main}
            compare={compare}
            onApply={(m, c) => {
              setMain(m);
              setCompare(c);
              setShowEditor(false);
            }}
          />
        )}

        {/* Column labels */}
        <div className="mb-2 hidden grid-cols-[1fr_128px_1fr] gap-4 lg:grid">
          <span
            className="inline-flex w-fit items-center rounded-sm border border-(--navy) bg-(--navy-bg) px-2.5 py-1 text-[11px] text-(--navy)"
            style={{ fontFamily: "var(--mono)" }}
          >
            main record
          </span>
          <span />
          <span
            className="inline-flex w-fit items-center rounded-sm border border-(--flag) bg-(--flag-bg) px-2.5 py-1 text-[11px] text-(--flag)"
            style={{ fontFamily: "var(--mono)" }}
          >
            compare record
          </span>
        </div>

        <main>
          <section className="mb-9">
            <SectionHeading
              title="Identity"
              note={`${scalarDiffs} field${scalarDiffs === 1 ? "" : "s"} differ`}
            />
            <ScalarRow
              label="Name"
              mainValue={main.name}
              compareValue={compare.name}
              onMainChange={(v) => setMain((prev) => ({ ...prev, name: v }))}
              onAdopt={() => adoptScalar("name")}
            />
            <ScalarRow
              label="Birthdate"
              mainValue={main.birthdate}
              compareValue={compare.birthdate}
              onMainChange={(v) => setMain((prev) => ({ ...prev, birthdate: v }))}
              onAdopt={() => adoptScalar("birthdate")}
              inputType="date"
            />
            <ScalarRow
              label="Description"
              mainValue={main.description}
              compareValue={compare.description}
              onMainChange={(v) => setMain((prev) => ({ ...prev, description: v }))}
              onAdopt={() => adoptScalar("description")}
              multiline
            />
          </section>

          <ArraySection<Address>
            title="Addresses"
            mainItems={main.addresses}
            compareItems={compare.addresses}
            renderSummary={renderAddress}
            renderEditable={renderAddressEditable}
            onMergeSelected={mergeAddresses}
            onReplaceAll={() =>
              setMain((prev) => ({ ...prev, addresses: cloneRecord(compare).addresses }))
            }
            onRemoveFromMain={(id) =>
              setMain((prev) => ({ ...prev, addresses: prev.addresses.filter((a) => a.id !== id) }))
            }
            onUpdateItem={(id, patch) =>
              setMain((prev) => ({
                ...prev,
                addresses: prev.addresses.map((a) => (a.id === id ? { ...a, ...patch } : a)),
              }))
            }
            onAddBlank={() =>
              setMain((prev) => ({ ...prev, addresses: [...prev.addresses, makeBlankAddress()] }))
            }
            addLabel="add address"
          />

          <ArraySection<AcademicRecord>
            title="Academic history"
            mainItems={main.academics}
            compareItems={compare.academics}
            renderSummary={renderAcademic}
            renderEditable={renderAcademicEditable}
            onMergeSelected={mergeAcademics}
            onReplaceAll={() =>
              setMain((prev) => ({ ...prev, academics: cloneRecord(compare).academics }))
            }
            onRemoveFromMain={(id) =>
              setMain((prev) => ({ ...prev, academics: prev.academics.filter((a) => a.id !== id) }))
            }
            onUpdateItem={(id, patch) =>
              setMain((prev) => ({
                ...prev,
                academics: prev.academics.map((a) => (a.id === id ? { ...a, ...patch } : a)),
              }))
            }
            onAddBlank={() =>
              setMain((prev) => ({ ...prev, academics: [...prev.academics, makeBlankAcademic()] }))
            }
            addLabel="add academic entry"
          />

          <ArraySection<UpdateHistoryEntry>
            title="Update history"
            mainItems={main.updateHistory}
            compareItems={compare.updateHistory}
            renderSummary={renderHistory}
            renderEditable={renderHistoryEditable}
            onMergeSelected={mergeHistory}
            onReplaceAll={() =>
              setMain((prev) => ({ ...prev, updateHistory: cloneRecord(compare).updateHistory }))
            }
            onRemoveFromMain={(id) =>
              setMain((prev) => ({
                ...prev,
                updateHistory: prev.updateHistory.filter((h) => h.id !== id),
              }))
            }
            onUpdateItem={(id, patch) =>
              setMain((prev) => ({
                ...prev,
                updateHistory: prev.updateHistory.map((h) => (h.id === id ? { ...h, ...patch } : h)),
              }))
            }
            onAddBlank={() =>
              setMain((prev) => ({
                ...prev,
                updateHistory: [...prev.updateHistory, makeBlankHistory()],
              }))
            }
            addLabel="add history entry"
          />
        </main>

        {/* Merged result preview */}
        <section className="mt-10 rounded-sm border border-(--rule) bg-(--paper-raised) p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2
              className="text-[15px] text-(--ink)"
              style={{ fontFamily: "var(--serif)" }}
            >
              Main record — live result
            </h2>
            <div className="flex gap-2">
              <button
                onClick={copyMerged}
                className="rounded-sm border border-(--rule) px-3 py-1.5 text-[11.5px] text-(--ink) transition-colors hover:border-(--navy)"
                style={{ fontFamily: "var(--mono)" }}
              >
                {copied ? "copied" : "copy json"}
              </button>
              <button
                onClick={downloadMerged}
                className="rounded-sm border border-(--navy) bg-(--navy) px-3 py-1.5 text-[11.5px] text-(--paper-raised) transition-opacity hover:opacity-90"
                style={{ fontFamily: "var(--mono)" }}
              >
                download .json
              </button>
            </div>
          </div>
          <pre
            className="max-h-80 overflow-auto rounded-sm bg-(--paper) p-3 text-[11.5px] leading-relaxed text-(--ink)"
            style={{ fontFamily: "var(--mono)" }}
          >
            {JSON.stringify(main, null, 2)}
          </pre>
        </section>
      </div>
    </div>
  );
}